import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Apple, GlassWater, Sofa, Smartphone, Home, User, Stethoscope, School,
  Utensils, Bus, Play, ChevronRight, Lightbulb, TypeIcon, ListChecks,
  BookOpen, Volume2, RotateCcw, Check, X, Sparkles, ArrowRight, Waypoints,
} from "lucide-react";
import {
  loadSpeak,
  recordBaselineSpeak,
  recordSpeakSession,
} from "@/lib/neuroSpeakStore";
import VoiceRecorder from "@/components/neurospeak/VoiceRecorder";

const getLang = () => (loadSpeak().profile?.language || "en");

// -----------------------------------------------------------------------
// NeuroSpeak training engines — non-diagnostic, rule-based, device-only.
//   • Baseline assessment (retrieval + recognition + sentence + functional)
//   • Word Retrieval with Graduated Cueing (0→4)
//   • Sentence Construction (word ordering)
//   • Picture → Word → Sentence (integrated demo)
// -----------------------------------------------------------------------

// Shared word/picture bank. Icon substitutes for a real photograph — a
// deliberate choice to keep the prototype device-only and offline-friendly.
// Localised labels for TTS + Whisper prompt in Tamil / Hindi.
export const WORD_BANK = [
  { word: "Apple", ta: "ஆப்பிள்", hi: "सेब", Icon: Apple, category: "food", hint: "You can eat this.", article: "an" },
  { word: "Water", ta: "தண்ணீர்", hi: "पानी", Icon: GlassWater, category: "food", hint: "You drink this when thirsty.", article: "" },
  { word: "Chair", ta: "நாற்காலி", hi: "कुर्सी", Icon: Sofa, category: "daily", hint: "You sit on this.", article: "a" },
  { word: "Phone", ta: "தொலைபேசி", hi: "फ़ोन", Icon: Smartphone, category: "daily", hint: "You use this to call people.", article: "a" },
  { word: "House", ta: "வீடு", hi: "घर", Icon: Home, category: "places", hint: "You live in this.", article: "a" },
  { word: "Mother", ta: "அம்மா", hi: "माँ", Icon: User, category: "family", hint: "A close family member.", article: "" },
  { word: "Doctor", ta: "மருத்துவர்", hi: "डॉक्टर", Icon: Stethoscope, category: "family", hint: "This person helps you when you are unwell.", article: "a" },
  { word: "School", ta: "பள்ளி", hi: "स्कूल", Icon: School, category: "places", hint: "Children go here to learn.", article: "" },
  { word: "Food", ta: "உணவு", hi: "खाना", Icon: Utensils, category: "food", hint: "You eat this every day.", article: "" },
  { word: "Bus", ta: "பேருந்து", hi: "बस", Icon: Bus, category: "daily", hint: "A big vehicle that carries many people.", article: "a" },
];

// Return the target word in the currently selected language.
export const targetFor = (item, lang) => {
  if (!item) return "";
  if (lang === "ta" && item.ta) return item.ta;
  if (lang === "hi" && item.hi) return item.hi;
  return item.word;
};

const normalise = (s) => (s || "").trim().toLowerCase();
const shuffle = (arr) => {
  const c = [...arr];
  for (let i = c.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [c[i], c[j]] = [c[j], c[i]];
  }
  return c;
};

// -----------------------------------------------------------------------
// Reusable Picture tile — an accessible, high-contrast "photograph" slot.
// -----------------------------------------------------------------------
export const Picture = ({ Icon, label, size = "lg", showLabel = false }) => {
  const dims =
    size === "sm"
      ? "w-24 h-24"
      : size === "md"
      ? "w-40 h-40"
      : "w-56 h-56 sm:w-64 sm:h-64";
  return (
    <div
      data-testid={`picture-${label}`}
      className={`${dims} rounded-3xl border border-white/10 bg-gradient-to-br from-cyan-400/15 via-emerald-400/10 to-violet-400/10 flex flex-col items-center justify-center gap-3`}
    >
      <Icon className="w-1/2 h-1/2 text-white" strokeWidth={1.4} />
      {showLabel && (
        <div className="text-xs uppercase tracking-widest text-zinc-400 font-semibold">
          {label}
        </div>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------
// Reusable ResultBanner
// -----------------------------------------------------------------------
const ResultBanner = ({ correct, total, cue = null, note = null }) => {
  const pct = total ? Math.round((correct / total) * 100) : 0;
  return (
    <div
      data-testid="speak-result"
      className="mt-4 rounded-2xl border border-emerald-400/30 bg-emerald-400/5 p-5"
    >
      <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
        Task result · non-diagnostic
      </div>
      <div className="mt-1 font-display text-2xl font-semibold tabular-nums">
        {correct}/{total} · {pct}%
      </div>
      {cue !== null && (
        <div className="mt-1 text-xs text-zinc-400">
          Assistance used: <span className="text-cyan-300 font-semibold">{cueLabel(cue)}</span>
        </div>
      )}
      {note && <div className="mt-1 text-xs text-zinc-400">{note}</div>}
    </div>
  );
};

const cueLabel = (lvl) =>
  ["Independent", "Semantic hint", "First letter", "Multiple choice", "Full model"][
    Math.min(4, lvl || 0)
  ];

// =======================================================================
// 1) BASELINE COMMUNICATION ASSESSMENT
//    Four short sub-tasks producing a "Communication Task Performance
//    Profile". Not a diagnosis. Not a clinical score.
// =======================================================================

const BASELINE_STEPS = [
  { key: "retrieval", label: "Word Retrieval", Icon: TypeIcon },
  { key: "recognition", label: "Word Recognition", Icon: ListChecks },
  { key: "sentence", label: "Sentence Construction", Icon: BookOpen },
  { key: "functional", label: "Functional Communication", Icon: Volume2 },
];

const FUNCTIONAL_PROMPTS = [
  { q: "What would you say if you wanted water?", accept: ["water", "i want water", "i need water"] },
  { q: "What would you say when greeting someone?", accept: ["hello", "hi", "namaste", "vanakkam", "namaskar"] },
  { q: "What would you say if you needed help?", accept: ["help", "help me", "i need help", "please help"] },
];

export function BaselineAssessment({ onDone }) {
  const [step, setStep] = useState(0);
  const [results, setResults] = useState({
    retrieval: { correct: 0, total: 0 },
    recognition: { correct: 0, total: 0 },
    sentence: { correct: 0, total: 0 },
    functional: { correct: 0, total: 0 },
  });
  const [done, setDone] = useState(false);

  const finishSub = (key, correct, total) => {
    const next = { ...results, [key]: { correct, total } };
    setResults(next);
    if (step + 1 >= BASELINE_STEPS.length) {
      recordBaselineSpeak(next);
      setDone(true);
      onDone?.(next);
    } else {
      setStep(step + 1);
    }
  };

  if (done) {
    const totalCorrect = Object.values(results).reduce((a, r) => a + r.correct, 0);
    const totalItems = Object.values(results).reduce((a, r) => a + r.total, 0);
    return (
      <div className="rounded-3xl border border-emerald-400/30 bg-emerald-400/5 p-6 sm:p-8">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
          Communication Task Performance Profile · Non-diagnostic
        </div>
        <h3 className="mt-1 font-display text-2xl sm:text-3xl font-semibold">
          Baseline saved.
        </h3>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
          {BASELINE_STEPS.map((s) => {
            const r = results[s.key];
            const pct = r.total ? Math.round((r.correct / r.total) * 100) : 0;
            return (
              <div
                key={s.key}
                data-testid={`baseline-summary-${s.key}`}
                className="rounded-2xl border border-white/10 bg-black/30 p-3"
              >
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                  {s.label}
                </div>
                <div className="mt-1 font-display text-2xl font-semibold tabular-nums text-white">
                  {r.correct}/{r.total || "—"}
                </div>
                <div className="text-xs text-zinc-500">{pct}%</div>
              </div>
            );
          })}
        </div>
        <p className="mt-4 text-xs text-zinc-400 max-w-2xl leading-relaxed">
          Overall: {totalCorrect}/{totalItems}. This is task performance in
          NeuroSpeak&apos;s sub-tests only, not a clinical assessment. A
          speech-language pathologist can help you interpret patterns.
        </p>
      </div>
    );
  }

  const S = BASELINE_STEPS[step];
  return (
    <div>
      <div className="mb-3 text-[10px] uppercase tracking-widest text-cyan-300 font-semibold flex items-center gap-2">
        Baseline · Step {step + 1} of {BASELINE_STEPS.length} · {S.label}
      </div>
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
        {S.key === "retrieval" && (
          <BaselineRetrieval onFinish={(c, t) => finishSub("retrieval", c, t)} />
        )}
        {S.key === "recognition" && (
          <BaselineRecognition onFinish={(c, t) => finishSub("recognition", c, t)} />
        )}
        {S.key === "sentence" && (
          <BaselineSentence onFinish={(c, t) => finishSub("sentence", c, t)} />
        )}
        {S.key === "functional" && (
          <BaselineFunctional onFinish={(c, t) => finishSub("functional", c, t)} />
        )}
      </div>
    </div>
  );
}

function BaselineRetrieval({ onFinish }) {
  const items = useMemo(() => shuffle(WORD_BANK).slice(0, 4), []);
  const [idx, setIdx] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [lang] = useState(() => getLang());
  const item = items[idx];
  const localTarget = targetFor(item, lang);

  const handleResult = (data) => {
    const ok = data.clarity === "clear" || data.similarity >= 70;
    if (ok) setCorrect((c) => c + 1);
    recordSpeakSession({
      kind: "retrieval",
      word: item.word,
      response: data.transcript,
      cue_level: 0,
      correct: ok,
    });
    setTimeout(() => {
      if (idx + 1 >= items.length) onFinish(correct + (ok ? 1 : 0), items.length);
      else setIdx(idx + 1);
    }, 1200);
  };

  return (
    <div>
      <p className="text-sm text-zinc-300 mb-4">
        Look at the picture and <b>say the word aloud</b>. Whisper AI will
        transcribe your speech and score clarity.
      </p>
      <div className="flex flex-col sm:flex-row items-center gap-5">
        <Picture Icon={item.Icon} label={item.word} />
        <div className="flex-1 w-full">
          <VoiceRecorder
            key={idx}
            target={localTarget}
            language={lang}
            onResult={handleResult}
            testId="baseline-retrieval-voice"
          />
          <div className="mt-3 text-xs text-zinc-500">
            Item {idx + 1} / {items.length}
          </div>
        </div>
      </div>
    </div>
  );
}

function BaselineRecognition({ onFinish }) {
  const items = useMemo(() => shuffle(WORD_BANK).slice(0, 4), []);
  const [idx, setIdx] = useState(0);
  const [correct, setCorrect] = useState(0);
  const item = items[idx];
  const choices = useMemo(() => {
    const others = shuffle(WORD_BANK.filter((w) => w.word !== item.word)).slice(0, 3);
    return shuffle([item, ...others]).map((x) => x.word);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  const pick = (choice) => {
    const ok = choice === item.word;
    if (ok) setCorrect((c) => c + 1);
    recordSpeakSession({
      kind: "recognition",
      word: item.word,
      response: choice,
      cue_level: 3,
      correct: ok,
    });
    if (idx + 1 >= items.length) onFinish(correct + (ok ? 1 : 0), items.length);
    else setIdx(idx + 1);
  };

  return (
    <div>
      <p className="text-sm text-zinc-300 mb-4">Which word matches the picture?</p>
      <div className="flex flex-col sm:flex-row items-center gap-5">
        <Picture Icon={item.Icon} label={item.word} size="md" />
        <div className="grid grid-cols-2 gap-2 flex-1 w-full">
          {choices.map((c) => (
            <button
              key={c}
              data-testid={`baseline-recognition-${c}`}
              onClick={() => pick(c)}
              className="rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 py-3 text-white text-lg"
            >
              {c}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-3 text-xs text-zinc-500">
        Item {idx + 1} / {items.length}
      </div>
    </div>
  );
}

function BaselineSentence({ onFinish }) {
  const SETS = useMemo(
    () => [
      { target: "I want water" },
      { target: "please help me" },
      { target: "I am tired" },
    ],
    []
  );
  const [idx, setIdx] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [lang] = useState(() => getLang());
  const it = SETS[idx];

  const handleResult = (data) => {
    const ok = data.clarity === "clear" || data.similarity >= 60;
    if (ok) setCorrect((c) => c + 1);
    recordSpeakSession({
      kind: "sentence",
      word: it.target,
      response: data.transcript,
      cue_level: 0,
      correct: ok,
    });
    setTimeout(() => {
      if (idx + 1 >= SETS.length) onFinish(correct + (ok ? 1 : 0), SETS.length);
      else setIdx(idx + 1);
    }, 1400);
  };

  return (
    <div>
      <p className="text-sm text-zinc-300 mb-4">
        Say the sentence aloud. Whisper AI will transcribe and score clarity.
      </p>
      <div className="rounded-2xl border border-emerald-400/30 bg-emerald-400/5 px-4 py-3">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
          Say this sentence
        </div>
        <div className="mt-1 font-display text-xl sm:text-2xl font-semibold text-white">
          {it.target}
        </div>
      </div>
      <div className="mt-3">
        <VoiceRecorder
          key={idx}
          target={it.target}
          language={lang}
          onResult={handleResult}
          testId="baseline-sentence-voice"
          ctaText="Tap to Speak"
          maxMs={9000}
        />
      </div>
      <div className="mt-3 text-xs text-zinc-500">
        Item {idx + 1} / {SETS.length}
      </div>
    </div>
  );
}

function BaselineFunctional({ onFinish }) {
  const [idx, setIdx] = useState(0);
  const [correct, setCorrect] = useState(0);
  const it = FUNCTIONAL_PROMPTS[idx];

  const handleResult = (data) => {
    const norm = (data.transcript || "").trim().toLowerCase();
    const ok = it.accept.some((a) => norm.includes(a));
    if (ok) setCorrect((c) => c + 1);
    recordSpeakSession({
      kind: "functional",
      word: it.q,
      response: data.transcript,
      cue_level: 0,
      correct: ok,
    });
    setTimeout(() => {
      if (idx + 1 >= FUNCTIONAL_PROMPTS.length)
        onFinish(correct + (ok ? 1 : 0), FUNCTIONAL_PROMPTS.length);
      else setIdx(idx + 1);
    }, 1500);
  };

  return (
    <div>
      <p className="text-sm text-zinc-300 mb-4">
        Answer the everyday communication question — <b>say your reply aloud</b>.
      </p>
      <div className="rounded-2xl border border-violet-400/30 bg-violet-400/5 px-4 py-3 text-white text-lg">
        {it.q}
      </div>
      <div className="mt-3">
        <VoiceRecorder
          key={idx}
          target=""
          language={getLang()}
          onResult={handleResult}
          testId="baseline-functional-voice"
          ctaText="Tap to Speak"
          maxMs={12000}
        />
      </div>
      <div className="mt-3 text-xs text-zinc-500">
        Item {idx + 1} / {FUNCTIONAL_PROMPTS.length}
      </div>
    </div>
  );
}

// =======================================================================
// 2) WORD RETRIEVAL + GRADUATED CUEING (0→4)
// =======================================================================

const CUE_STEPS = [
  { level: 1, label: "Semantic hint", tone: "cyan" },
  { level: 2, label: "First letter", tone: "violet" },
  { level: 3, label: "Multiple choice", tone: "amber" },
  { level: 4, label: "Full model answer", tone: "red" },
];

export function WordRetrieval({ level = 1, onDone, onFeedback }) {
  const items = useMemo(
    () => shuffle(WORD_BANK).slice(0, 5 + Math.min(3, level)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [idx, setIdx] = useState(0);
  const [cue, setCue] = useState(0);
  const [sessionCorrect, setSessionCorrect] = useState(0);
  const [sessionCue, setSessionCue] = useState(0);
  const [done, setDone] = useState(false);
  const [feedback, setFeedback] = useState(null); // "correct" | "try-again"
  const [attemptNo, setAttemptNo] = useState(0);
  const [lang] = useState(() => getLang());
  const startedAt = useRef(performance.now());
  const item = items[idx];
  const localTarget = targetFor(item, lang);

  const bump = () => setCue((c) => Math.min(4, c + 1));

  const handleVoice = (data) => {
    // clarity "clear" means Whisper matched target; "close" is 70%+.
    const ok = data.clarity === "clear" || data.similarity >= 70;
    setFeedback(ok ? "correct" : "try-again");
    const rt = Math.round(performance.now() - startedAt.current);
    recordSpeakSession({
      kind: "retrieval",
      word: item.word,
      response: data.transcript,
      cue_level: cue,
      correct: ok,
      reaction_ms: rt,
    });
    if (ok) {
      setSessionCorrect((n) => n + 1);
      setSessionCue((n) => n + cue);
      setTimeout(() => next(), 1200);
    } else {
      // Escalate cue level up to 4 (full model). Reset attempt counter.
      setTimeout(() => {
        bump();
        setFeedback(null);
        setAttemptNo((n) => n + 1);
      }, 1200);
    }
  };

  const modelReveal = () => {
    setCue(4);
    setAttemptNo((n) => n + 1);
  };

  const next = () => {
    setFeedback(null);
    setAttemptNo(0);
    if (idx + 1 >= items.length) {
      setDone(true);
      onDone?.({
        correct: sessionCorrect,
        total: items.length,
        avgCue: sessionCue / Math.max(1, sessionCorrect || 1),
      });
      onFeedback?.();
    } else {
      setIdx(idx + 1);
      setCue(0);
      startedAt.current = performance.now();
    }
  };

  const skip = () => {
    recordSpeakSession({
      kind: "retrieval",
      word: item.word,
      response: "",
      cue_level: 4,
      correct: false,
    });
    next();
  };

  const firstLetter = localTarget[0] || item.word[0];
  const distractors = useMemo(
    () => shuffle(WORD_BANK.filter((w) => w.word !== item.word)).slice(0, 3).map((w) => targetFor(w, lang)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item.word, lang]
  );
  const mcOptions = useMemo(() => shuffle([localTarget, ...distractors]), [distractors, localTarget]);

  if (done) {
    return (
      <ResultBanner
        correct={sessionCorrect}
        total={items.length}
        note="Word retrieval session complete."
      />
    );
  }

  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold inline-flex items-center gap-1.5">
          <TypeIcon className="w-3.5 h-3.5" /> Word retrieval · voice
        </div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
          Item {idx + 1} / {items.length} · Cue {cueLabel(cue)}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-start gap-5">
        <Picture Icon={item.Icon} label={item.word} />
        <div className="flex-1 w-full min-w-0">
          {/* Cue progression (shown ABOVE recorder as guidance) */}
          {cue >= 1 && (
            <div
              data-testid="cue-semantic"
              className="mb-2 rounded-2xl border border-cyan-400/30 bg-cyan-400/5 px-4 py-2 text-sm text-cyan-100 inline-flex items-center gap-2"
            >
              <Lightbulb className="w-4 h-4" /> {item.hint}
            </div>
          )}
          {cue >= 2 && (
            <div
              data-testid="cue-first-letter"
              className="mb-2 rounded-2xl border border-violet-400/30 bg-violet-400/5 px-4 py-2 text-sm text-violet-100 inline-flex items-center gap-2"
            >
              Starts with:{" "}
              <span className="font-display text-lg font-semibold text-white">
                {firstLetter}
              </span>
              …
            </div>
          )}
          {cue >= 3 && (
            <div className="mb-2" data-testid="cue-mc">
              <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold mb-1">
                Multiple choice hint
              </div>
              <div className="grid grid-cols-2 gap-2">
                {mcOptions.map((o) => (
                  <div
                    key={o}
                    className={`rounded-xl border px-3 py-2 text-sm text-center ${
                      o === localTarget
                        ? "border-amber-400/50 bg-amber-400/15 text-white"
                        : "border-white/10 bg-white/5 text-zinc-300"
                    }`}
                  >
                    {o}
                  </div>
                ))}
              </div>
            </div>
          )}
          {cue >= 4 && (
            <div
              data-testid="cue-model"
              className="mb-2 rounded-2xl border border-red-400/30 bg-red-400/5 px-4 py-2 text-sm text-red-100"
            >
              The word is{" "}
              <span className="font-display text-lg font-semibold text-white">
                {localTarget}
              </span>
              . Say it aloud to move on.
            </div>
          )}

          {/* Voice recorder — the primary input */}
          <VoiceRecorder
            key={`${idx}-${attemptNo}`}
            target={localTarget}
            language={lang}
            onResult={handleVoice}
            testId="retrieval-voice"
            ctaText="Tap to Speak"
          />

          {/* Feedback */}
          {feedback === "correct" && (
            <div
              data-testid="retrieval-feedback-correct"
              className="mt-3 inline-flex items-center gap-2 text-sm text-emerald-300"
            >
              <Check className="w-4 h-4" /> Great — clear match.
            </div>
          )}
          {feedback === "try-again" && (
            <div className="mt-3 inline-flex items-center gap-2 text-sm text-amber-300">
              <X className="w-4 h-4" /> Not quite — trying again with a hint.
            </div>
          )}

          {/* Controls */}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {cue < 3 && (
              <button
                data-testid="retrieval-need-hint"
                onClick={bump}
                className="rounded-full border border-white/10 bg-white/5 hover:bg-white/10 px-4 py-2 text-xs text-white inline-flex items-center gap-1"
              >
                <Lightbulb className="w-3.5 h-3.5" /> Need a hint
              </button>
            )}
            {cue < 4 && (
              <button
                data-testid="retrieval-model"
                onClick={modelReveal}
                className="rounded-full border border-white/10 bg-white/5 hover:bg-white/10 px-4 py-2 text-xs text-white"
              >
                Show me the word
              </button>
            )}
            <button
              data-testid="retrieval-skip"
              onClick={skip}
              className="text-xs text-zinc-500 hover:text-white px-3 py-2"
            >
              Skip
            </button>
          </div>

          {/* Cue key */}
          <div className="mt-4 text-[10px] uppercase tracking-widest text-zinc-500">
            Assistance path
          </div>
          <div className="mt-1 flex items-center flex-wrap gap-1.5">
            {["Independent", "Hint", "1st letter", "Choices", "Model"].map(
              (l, i) => (
                <span
                  key={l}
                  className={`text-[10px] uppercase font-semibold rounded-full px-2 py-0.5 border ${
                    cue >= i
                      ? "border-cyan-400 bg-cyan-400/20 text-white"
                      : "border-white/10 bg-white/5 text-zinc-500"
                  }`}
                >
                  {i}. {l}
                </span>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// =======================================================================
// 3) SENTENCE CONSTRUCTION training (progressive complexity)
// =======================================================================

const SENTENCE_LEVELS = {
  1: [
    { target: "I want water", cue: "Ask for water." },
    { target: "I am tired", cue: "Say you are tired." },
    { target: "please help me", cue: "Ask for help." },
  ],
  2: [
    { target: "I want some water", cue: "Ask for a little water." },
    { target: "I feel very tired", cue: "Say you feel very tired." },
    { target: "please call the doctor", cue: "Ask someone to call the doctor." },
  ],
  3: [
    { target: "I want some water because I am thirsty", cue: "Explain why you want water." },
    { target: "please help me stand up carefully", cue: "Ask for help standing." },
    { target: "I would like to talk to my daughter", cue: "Say who you want to talk to." },
  ],
};

const distractorPool = ["quickly", "later", "very", "big", "small", "today", "some", "a", "the"];

export function SentenceConstruction({ level = 1, onDone, onFeedback }) {
  const set = SENTENCE_LEVELS[Math.min(3, level)] || SENTENCE_LEVELS[1];
  const [idx, setIdx] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [done, setDone] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [lang] = useState(() => getLang());
  const startedAt = useRef(performance.now());
  const it = set[idx];

  const handleVoice = (data) => {
    const heard = (data.transcript || "").trim();
    setTranscript(heard);
    // Sentences are longer — the backend similarity already handles fuzziness.
    // 60%+ counts as a successful attempt at sentence level.
    const ok = data.clarity === "clear" || data.similarity >= 60;
    setFeedback(ok ? "correct" : "try-again");
    if (ok) setCorrect((c) => c + 1);
    recordSpeakSession({
      kind: "sentence",
      word: it.target,
      response: heard,
      cue_level: 0,
      correct: ok,
      reaction_ms: Math.round(performance.now() - startedAt.current),
    });
    setTimeout(() => {
      setFeedback(null);
      setTranscript("");
      if (idx + 1 >= set.length) {
        setDone(true);
        onDone?.({ correct: correct + (ok ? 1 : 0), total: set.length });
        onFeedback?.();
      } else {
        setIdx(idx + 1);
        startedAt.current = performance.now();
      }
    }, 1600);
  };

  if (done) return <ResultBanner correct={correct} total={set.length} />;

  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold inline-flex items-center gap-1.5">
          <BookOpen className="w-3.5 h-3.5" /> Sentence construction · voice · Level {level}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
          Item {idx + 1} / {set.length}
        </div>
      </div>
      <div className="mt-1 text-sm text-zinc-300">
        <span className="text-cyan-300 font-semibold">Cue: </span>
        {it.cue}
      </div>

      <div className="mt-4 rounded-2xl border border-emerald-400/30 bg-emerald-400/5 p-4">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
          Say this sentence aloud
        </div>
        <div className="mt-1 font-display text-xl sm:text-2xl font-semibold text-white leading-snug">
          {it.target}
        </div>
      </div>

      <div className="mt-4">
        <VoiceRecorder
          key={idx}
          target={it.target}
          language={lang}
          onResult={handleVoice}
          testId="sc-voice"
          ctaText="Tap to Speak"
          maxMs={12000}
        />
      </div>

      {feedback === "correct" && (
        <div className="mt-3 inline-flex items-center gap-2 text-sm text-emerald-300">
          <Check className="w-4 h-4" /> Great — clear match.
        </div>
      )}
      {feedback === "try-again" && (
        <div className="mt-3 inline-flex items-center gap-2 text-sm text-amber-300">
          <X className="w-4 h-4" /> Not quite — try again slowly.
        </div>
      )}
      {transcript && (
        <div className="mt-2 text-xs text-zinc-400">
          You said: <span className="text-white">{transcript}</span>
        </div>
      )}
    </div>
  );
}

// =======================================================================
// 4) PICTURE → WORD → SENTENCE
// =======================================================================

export function PictureWordSentence({ onDone, onFeedback }) {
  const item = useMemo(() => WORD_BANK[Math.floor(Math.random() * WORD_BANK.length)], []);
  const [phase, setPhase] = useState("word"); // word -> sentence -> done
  const [lang] = useState(() => getLang());
  const localTarget = targetFor(item, lang);
  const sentenceTarget = `I want ${localTarget}`;
  const [heardSentence, setHeardSentence] = useState("");

  const handleWordVoice = (data) => {
    const ok = data.clarity === "clear" || data.similarity >= 70;
    recordSpeakSession({
      kind: "picture-word-sentence",
      word: item.word,
      response: data.transcript,
      cue_level: 0,
      correct: ok,
    });
    if (ok) setTimeout(() => setPhase("sentence"), 1000);
  };

  const handleSentenceVoice = (data) => {
    const heard = (data.transcript || "").trim();
    setHeardSentence(heard);
    const ok = data.clarity === "clear" || data.similarity >= 55;
    recordSpeakSession({
      kind: "sentence",
      word: sentenceTarget,
      response: heard,
      cue_level: 0,
      correct: ok,
    });
    if (ok) {
      // Speak it back for confidence.
      try {
        const u = new window.SpeechSynthesisUtterance(heard || sentenceTarget);
        u.rate = 0.95;
        window.speechSynthesis.speak(u);
      } catch {
        /* ignore */
      }
      setTimeout(() => {
        setPhase("done");
        onDone?.({ correct: 1, total: 1 });
        onFeedback?.();
      }, 1400);
    }
  };

  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
      <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold inline-flex items-center gap-1.5">
        <Waypoints className="w-3.5 h-3.5" /> Picture → Word → Sentence · voice
      </div>
      <h3 className="mt-1 font-display text-2xl sm:text-3xl font-semibold">
        From picture, to word, to <span className="italic text-emerald-300">voice.</span>
      </h3>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-[auto,1fr] gap-6 items-start">
        <Picture Icon={item.Icon} label={item.word} />
        <div className="min-w-0">
          <StepIndicator
            steps={["Word", "Sentence", "Done"]}
            active={phase === "word" ? 0 : phase === "sentence" ? 1 : 2}
          />
          {phase === "word" && (
            <div className="mt-4">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                What is this? Say it aloud.
              </label>
              <div className="mt-2">
                <VoiceRecorder
                  target={localTarget}
                  language={lang}
                  onResult={handleWordVoice}
                  testId="pws-word-voice"
                  ctaText="Tap to Speak"
                />
              </div>
            </div>
          )}

          {phase === "sentence" && (
            <div className="mt-4">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                Now say the sentence aloud
              </label>
              <div className="mt-2 rounded-2xl border border-emerald-400/30 bg-emerald-400/5 p-4">
                <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
                  Say
                </div>
                <div className="mt-1 font-display text-2xl font-semibold text-white">
                  {sentenceTarget}
                </div>
              </div>
              <div className="mt-3">
                <VoiceRecorder
                  target={sentenceTarget}
                  language={lang}
                  onResult={handleSentenceVoice}
                  testId="pws-sentence-voice"
                  ctaText="Tap to Speak"
                  maxMs={9000}
                />
              </div>
              {heardSentence && (
                <div className="mt-2 text-xs text-zinc-400">
                  You said: <span className="text-white">{heardSentence}</span>
                </div>
              )}
            </div>
          )}

          {phase === "done" && (
            <div className="mt-4">
              <ResultBanner correct={1} total={1} note="Full picture → word → sentence flow complete." />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const StepIndicator = ({ steps, active }) => (
  <div className="flex items-center gap-1">
    {steps.map((s, i) => (
      <div key={s} className="flex items-center gap-1">
        <span
          className={`text-[10px] uppercase font-semibold tracking-widest rounded-full px-2 py-0.5 border ${
            i === active
              ? "border-cyan-400 bg-cyan-400/20 text-white"
              : i < active
              ? "border-emerald-400 bg-emerald-400/15 text-emerald-100"
              : "border-white/10 bg-white/5 text-zinc-500"
          }`}
        >
          {i + 1}. {s}
        </span>
        {i < steps.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-zinc-600" />}
      </div>
    ))}
  </div>
);
